import { isEqual } from 'lodash'

import { PricingTierSettings } from '../../pricing-tier/columns'

import { PricingTierComponents } from '@/api'
import { FieldType } from '@/models'

type PricingTier = PricingTierComponents.Schemas.PricingTier

/** a tier setting is a switch (`enabled`) or a limit (`quota`) */
export type PricingTierValue = {
  enabled?: boolean
  quota?: number | null
}

export type PricingTierRow = {
  key: string
  label: string
  type: number
  /** value from the tier itself, or the field default when the tier doesn't set it */
  tierValue: PricingTierValue
  /** what applies to this org: the override when there is one, else the tier value */
  value: PricingTierValue
  /** the org's own value, if any; can equal the tier value */
  override?: PricingTierValue
  overridden: boolean
}

/** one row per tier setting (the tier name aside), with this org's override applied */
export const getPricingTierRows = (
  pricingTier?: PricingTier
): PricingTierRow[] =>
  PricingTierSettings.filter((field) => field.key !== 'name').map((field) => {
    const tierValue = (pricingTier?.settings?.[field.key] ??
      field.default) as PricingTierValue
    const override = pricingTier?.override_settings?.[field.key] as
      | PricingTierValue
      | undefined

    return {
      key: field.key,
      label: field.label,
      type: field.type,
      tierValue,
      value: override ?? tierValue,
      override,
      overridden: override !== undefined && !isEqual(override, tierValue)
    }
  })

export const isLimitRow = (row: PricingTierRow) =>
  row.type === FieldType.NUMBER_FIELD

export const isCapabilityRow = (row: PricingTierRow) =>
  row.type === FieldType.SWITCH

export const formatQuota = (value?: PricingTierValue) => {
  const quota = value?.quota

  if (quota === undefined || quota === null || Number.isNaN(quota)) return '—'
  if (quota === -1) return 'Unlimited'

  return quota.toLocaleString('en-US')
}

const CAPABILITY_GROUPS: { label: string; keys: string[] }[] = [
  {
    label: 'Authentication',
    keys: ['sso', 'advanced_auth_portals', 'portal_sso']
  },
  {
    label: 'Portals',
    keys: [
      'customer_portal',
      'installer_portal',
      'account_self_management',
      'permissions_on_attribute_level_in_ecp'
    ]
  },
  {
    label: 'Journeys',
    keys: [
      'tracking_in_journey',
      'share_journey',
      'journey_context_parameters',
      'journey_links',
      'product_availability_service'
    ]
  },
  {
    label: 'Automation',
    keys: ['webhook', 'trigger_automation_in_wf', 'automation_conditions']
  },
  {
    label: 'Data & permissions',
    keys: [
      'attribute_permissions',
      'role_based_rules',
      'audit_log',
      'entity_address_maps',
      'metering'
    ]
  },
  {
    label: 'Messaging & design',
    keys: ['bulk_messages', 'custom_design', 'visual_template_builder']
  },
  { label: 'AI', keys: ['ai_message_labeling', 'ai_partner_assignment'] }
]

/** groups capability rows by area; settings no group lists end up in Other */
export const groupCapabilities = (rows: PricingTierRow[]) => {
  const byKey = new Map(rows.map((row) => [row.key, row]))
  const grouped = new Set(CAPABILITY_GROUPS.flatMap((group) => group.keys))

  return [
    ...CAPABILITY_GROUPS.map((group) => ({
      label: group.label,
      rows: group.keys
        .map((key) => byKey.get(key))
        .filter((row): row is PricingTierRow => Boolean(row))
    })),
    { label: 'Other', rows: rows.filter((row) => !grouped.has(row.key)) }
  ].filter((group) => group.rows.length > 0)
}
